import React, {useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Trans, useTranslation} from 'react-i18next';
import Dropzone from 'react-dropzone';
import _isEmpty from 'lodash/isEmpty';
import InfoDialog from '../../../../components/dialogs/InfoDialog';
import FileUploader from '../../../../components/fileupload/FileUploader';
import Loader from '../../../../components/spinner/Loader';
import ActionButton from '../../../../components/datatable/ActionButton';
import classnames from 'classnames';
import {
  exportTemplate,
  exportProducts,
  importProducts,
  uploadProducts,
  updateProducts,
} from './BatchImportAndExport';
import {toast} from 'react-toastify';
import UploadResultMessage from './UploadResultMessage';
import OverLayLoader from '../../../../components/spinner/OverLayLoader';

export default function AddOrEditBatchImportDialog({
  show,
  setShow,
  action,
  type,
  categories = [],
  productCount,
  totalQuota,
  brandName,
  token,
  customerId,
  processedParams,
  refetch,
}) {
  const {t} = useTranslation();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [tip, setTip] = useState('default');
  const [validateErrors, setValidateErrors] = useState({});
  const [uploadResponse, setUploadResponse] = useState([]);
  const [finishUpload, setFinishUpload] = useState(false);
  const [completeData, setCompleteData] = useState(null);
  const [readyToUpload, setReadyToUpload] = useState(false);
  const [rows, setRows] = useState('0');
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const isEdit = action === 'edit';

  const closeModal = () => {
    setShow(false);
    setFile(null);
    setReadyToUpload(false);
    setStep(1);
    setTip('default');
    setValidateErrors({});
    setUploadResponse([]);
    setFinishUpload(false);
    setRows('0');
  };

  const renderErrorMessages = errorData => {
    if (!errorData || !errorData.errors) return [];

    const messages = [];
    const {errorType, errorMessage, errors} = errorData;

    // Validation errors
    if (errorType === 'file' || errorType === 'exception') {
      messages.push(errorMessage);
      return messages;
    }

    if (errorType === 'header') {
      Object.keys(errors).forEach(sheetKey => {
        const sheet = errors[sheetKey];
        const sheetName =
          sheetKey === 'basicInfo'
            ? 'Basic Info'
            : sheetKey === 'localization'
            ? type === 'treatment'
              ? 'Treatment Localization'
              : 'Product Localization'
            : sheetKey;

        if (sheet.noSheet) {
          messages.push(`Missing sheet: ${sheetName}`);
        }
        if (sheet.missing && sheet.missing.length > 0) {
          messages.push(
            `Missing columns in ${sheetName}: ${sheet.missing.join(', ')}`
          );
        }
        if (sheet.unsupported && sheet.unsupported.length > 0) {
          messages.push(
            `Unsupported columns in ${sheetName}: ${sheet.unsupported.join(
              ', '
            )}`
          );
        }
        if (sheet.firstRow && sheet.firstRow.length > 0) {
          messages.push(
            `Columns not in first row: ${sheet.firstRow.join(', ')}`
          );
        }
      });
      return messages;
    }

    if (errorType === 'quota') {
      messages.push(
        `The total number of Skincare Products, Routines, and Treatments cannot exceed ${totalQuota}. Please adjust your batch upload accordingly.`
      );
      return messages;
    }

    if (errorType === 'preview') {
      const {basicInfo, localization, category, language} = errors;

      // 1. Basic Info errors
      if (basicInfo) {
        if (basicInfo.noData && basicInfo.noData.length > 0) {
          messages.push('Basic Info sheet has no data rows');
        }
        if (basicInfo.isBlank && basicInfo.isBlank.length > 0) {
          messages.push(
            `Required fields are blank: Row ${basicInfo.isBlank.join(',')}`
          );
        }
        if (basicInfo.invalidCategory && basicInfo.invalidCategory.length > 0) {
          messages.push(
            `Invalid category: Row ${basicInfo.invalidCategory.join(',')}`
          );
        }
        if (basicInfo.invalidLanguage && basicInfo.invalidLanguage.length > 0) {
          messages.push(
            `Invalid language: Row ${basicInfo.invalidLanguage.join(',')}`
          );
        }
        if (
          basicInfo.invalidActionUrl &&
          basicInfo.invalidActionUrl.length > 0
        ) {
          messages.push(
            `Invalid Action URL: Row ${basicInfo.invalidActionUrl.join(',')}`
          );
        }
        if (basicInfo.invalidImageUrl && basicInfo.invalidImageUrl.length > 0) {
          messages.push(
            `Invalid image URL format: Row ${basicInfo.invalidImageUrl.join(
              ','
            )}`
          );
        }
        if (basicInfo.imageDimension && basicInfo.imageDimension.length > 0) {
          messages.push(
            `Image dimension must be 350x350: Row ${basicInfo.imageDimension.join(
              ','
            )}`
          );
        }
        if (basicInfo.imageUpload && basicInfo.imageUpload.length > 0) {
          messages.push(
            `Image upload failed: Row ${basicInfo.imageUpload.join(',')}`
          );
        }
        if (basicInfo.imageSize && basicInfo.imageSize.length > 0) {
          messages.push(
            `Image size exceeds 10MB: Row ${basicInfo.imageSize.join(',')}`
          );
        }
        if (
          basicInfo.productNameLength &&
          basicInfo.productNameLength.length > 0
        ) {
          const entityName = type === 'treatment' ? 'Treatment' : 'Product';
          messages.push(
            `${entityName} name exceeds 200 characters: Row ${basicInfo.productNameLength.join(
              ','
            )}`
          );
        }
        if (
          basicInfo.productDescLength &&
          basicInfo.productDescLength.length > 0
        ) {
          const entityName = type === 'treatment' ? 'Treatment' : 'Product';
          messages.push(
            `${entityName} description exceeds 500 characters: Row ${basicInfo.productDescLength.join(
              ','
            )}`
          );
        }
        // 2. Duplicate Product ID / Treatment ID
        if (
          basicInfo.duplicateProductId &&
          basicInfo.duplicateProductId.length > 0
        ) {
          const idName = type === 'treatment' ? 'Treatment ID' : 'Product ID';
          messages.push(
            `Duplicate ${idName} found: Row ${basicInfo.duplicateProductId.join(
              ','
            )}`
          );
        }
      }

      // 3. Product Localization / Treatment Localization errors
      if (localization) {
        const localizationName =
          type === 'treatment'
            ? 'Treatment Localization'
            : 'Product Localization';
        const idName = type === 'treatment' ? 'Treatment ID' : 'Product ID';
        const entityName = type === 'treatment' ? 'Treatment' : 'Product';

        if (localization.noData && localization.noData.length > 0) {
          messages.push(`${localizationName} sheet has no data rows`);
        }
        if (localization.isBlank && localization.isBlank.length > 0) {
          messages.push(
            `${localizationName} - Required fields are blank: Row ${localization.isBlank.join(
              ','
            )}`
          );
        }
        if (
          localization.invalidProductId &&
          localization.invalidProductId.length > 0
        ) {
          messages.push(
            `${localizationName} - ${idName} not found in Basic Info: Row ${localization.invalidProductId.join(
              ','
            )}`
          );
        }
        if (
          localization.invalidLanguage &&
          localization.invalidLanguage.length > 0
        ) {
          messages.push(
            `${localizationName} - Invalid language: Row ${localization.invalidLanguage.join(
              ','
            )}`
          );
        }
        if (
          localization.productNameLength &&
          localization.productNameLength.length > 0
        ) {
          messages.push(
            `${localizationName} - ${entityName} name exceeds 200 characters: Row ${localization.productNameLength.join(
              ','
            )}`
          );
        }
        if (
          localization.productDescLength &&
          localization.productDescLength.length > 0
        ) {
          messages.push(
            `${localizationName} - ${entityName} description exceeds 500 characters: Row ${localization.productDescLength.join(
              ','
            )}`
          );
        }
      }

      // 4. Category sheet errors
      if (category) {
        if (category.noSheet) {
          messages.push('Missing sheet: Category');
        }
        if (category.empty && category.empty.length > 0) {
          messages.push(...category.empty);
        }
        if (category.isBlank && category.isBlank.length > 0) {
          messages.push(
            `Category sheet has blank cells: Row ${category.isBlank.join(',')}`
          );
        }
        if (category.invalidCategory && category.invalidCategory.length > 0) {
          messages.push(
            `Category sheet has invalid categories: ${category.invalidCategory}`
          );
        }
      }

      // 5. Language sheet errors
      if (language) {
        if (language.noSheet) {
          messages.push('Missing sheet: Language');
        }
        if (language.empty && language.empty.length > 0) {
          messages.push(...language.empty);
        }
        if (language.isBlank && language.isBlank.length > 0) {
          messages.push(
            `Language sheet has blank cells: Row ${language.isBlank.join(',')}`
          );
        }
        if (language.invalidLanguage && language.invalidLanguage.length > 0) {
          messages.push(
            `Language sheet has invalid languages: ${language.invalidLanguage}`
          );
        }
      }
    }

    return messages;
  };

  const tipSection = tip => {
    switch (tip) {
      case 'success':
        return (
          <>
            <div className="kt-margin-t-10 text-success">
              <Trans i18nKey="p.c.c.batch.import.upload.success">
                The file upload was successful. Click "Next" to verify if the
                data meets the specifications.
              </Trans>
            </div>
            {!_isEmpty(file) && (
              <div className="d-flex kt-margin-t-10">
                <Trans i18nKey="p.c.c.batch.import.file.name">File Name:</Trans>
                <a
                  href={URL.createObjectURL(file)}
                  className="kt-margin-l-10"
                  style={{textDecoration: 'underline'}}
                >
                  {file.path || file.name}
                </a>
              </div>
            )}
          </>
        );
      case 'fail':
        return (
          <div className="kt-margin-t-10 text-danger">
            <Trans i18nKey="p.c.c.batch.import.upload.fail">
              Something went wrong with the file, please check it again.
            </Trans>
          </div>
        );
      default:
        return <></>;
    }
  };

  const handleExport = async () => {
    try {
      await exportProducts(
        isEdit,
        categories,
        totalQuota,
        token,
        customerId,
        processedParams,
        type
      );
    } catch (error) {
      console.error('Fail to export products/treatments:', error);
      toast.error(
        <Trans i18nKey="toast.error.loading.data.failed">
          Failed to load data.
        </Trans>
      );
    }
  };

  const validateFile = async (file, type) => {
    setValidateErrors({});
    setIsValidating(true);
    setReadyToUpload(false);
    try {
      const res = await importProducts(
        file,
        type,
        isEdit,
        productCount,
        totalQuota,
        categories,
        brandName,
        token,
        customerId
      );

      if (res.errorType) {
        setValidateErrors(res);
        setCompleteData(null);
        setReadyToUpload(false);
      } else {
        setValidateErrors({});
        setCompleteData(res.completeData);
        setReadyToUpload(true);

        if (res.completeData && res.completeData.length > 0) {
          setRows(res.completeData.length);
        }
      }
    } catch (error) {
      console.error('Fail to validate file:', error);
      setValidateErrors({
        errorType: 'exception',
        errorMessage:
          'Unable to process the file. Please check the file format and try again.',
        errors: {},
      });
      setCompleteData(null);
      setReadyToUpload(false);
    } finally {
      setIsValidating(false);
    }
  };

  const uploadFile = async completeData => {
    setUploadResponse([]);
    setFinishUpload(false);
    setIsUploading(true);

    try {
      const results = await (isEdit ? updateProducts : uploadProducts)(
        completeData,
        token,
        customerId,
        type
      );
      setUploadResponse(results);
      setFinishUpload(true);
    } catch (error) {
      console.error('Fail to upload file', error);
      setUploadResponse([]);
      setFinishUpload(false);
      setReadyToUpload(false);

      toast.error(
        <Trans i18nKey="p.s.addoreditcategory.error.failed">Failed</Trans>
      );
    } finally {
      setIsUploading(false);
    }
  };

  const step1Button = () => {
    if (action === 'add') {
      return (
        <ActionButton
          type={'customize'}
          disableDesc={
            <Trans i18nKey="p.s.listcustomer.export.client.no.permission">
              You have no permissions to export data.
            </Trans>
          }
          customize={{
            className: classnames({
              btn: true,
              'btn-primary': true,
            }),
            style: {width: '100%', height: '50px'},
            text: (
              <div className="d-flex">
                <div>
                  <i className="fa fa-download" style={{fontSize: '28px'}} />
                </div>
                <div
                  className="text-center"
                  style={{width: '100%', fontSize: '20px'}}
                >
                  <Trans
                    i18nKey="p.s.addskincare.batch.import.download"
                    values={{
                      type:
                        type === 'product'
                          ? t(
                              'p.s.addskincare.batch.import.product.title.singular',
                              {
                                defaultValue: 'Product',
                              }
                            )
                          : t(
                              'p.s.addskincare.batch.import.treatment.title.singular',
                              {
                                defaultValue: 'Treatment',
                              }
                            ),
                    }}
                  >
                    Download the Skincare {{type}} Template
                  </Trans>
                </div>
              </div>
            ),
          }}
          onClick={() => exportTemplate(false, categories, type)}
        />
      );
    } else {
      return (
        <ActionButton
          type={'customize'}
          disableDesc={
            <Trans i18nKey="p.s.listcustomer.export.client.no.permission">
              You have no permissions to export data.
            </Trans>
          }
          customize={{
            className: classnames({
              btn: true,
              'btn-primary': true,
            }),
            style: {width: '100%', height: '50px'},
            text: (
              <div className="d-flex">
                <div>
                  <i className="fa fa-download" style={{fontSize: '28px'}} />
                </div>
                <div
                  className="text-center"
                  style={{width: '100%', fontSize: '20px'}}
                >
                  <Trans
                    i18nKey="p.s.addskincare.batch.import.export"
                    values={{
                      type:
                        type === 'product'
                          ? t(
                              'p.s.addskincare.batch.import.product.title.plural',
                              {
                                defaultValue: 'Products',
                              }
                            )
                          : t(
                              'p.s.addskincare.batch.import.treatment.title.plural',
                              {
                                defaultValue: 'Treatments',
                              }
                            ),
                    }}
                  >
                    Export the Skincare {{type}}
                  </Trans>
                </div>
              </div>
            ),
          }}
          onClick={handleExport}
        />
      );
    }
  };

  return (
    <>
      <Modal
        size={'lg'}
        show={show}
        onHide={setShow}
        centered={true}
        backdrop={'static'}
        enforceFocus={true}
      >
        <Modal.Header>
          <Modal.Title>
            <Trans
              i18nKey="p.s.addskincare.batch.import.title"
              values={{
                action:
                  action === 'add'
                    ? t('p.s.addskincare.batch.import.add', {
                        defaultValue: 'Add',
                      })
                    : t('p.s.addskincare.batch.import.edit', {
                        defaultValue: 'Edit',
                      }),
                type:
                  type === 'product'
                    ? t('p.s.addskincare.batch.import.product.title.plural', {
                        defaultValue: 'Products',
                      })
                    : t('p.s.addskincare.batch.import.treatment.title.plural', {
                        defaultValue: 'Treatments',
                      }),
              }}
            >
              Batch {{action}} {{type}}
            </Trans>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {action === 'add' ? (
            <Trans
              i18nKey="p.s.addskincare.batch.import.add.notice.desc"
              values={{
                type:
                  type === 'product'
                    ? t('p.s.addskincare.batch.import.product.plural', {
                        defaultValue: 'products',
                      })
                    : t('p.s.addskincare.batch.import.treatment.plural', {
                        defaultValue: 'treatments',
                      }),
              }}
            >
              Kindly download the provided sample file, complete the necessary
              details, and upload it to efficiently add {{type}} in batches.
              Supported file formats include .xlsx and zip (a compressed format
              containing xlsx and image files).
            </Trans>
          ) : (
            <Trans
              i18nKey="p.s.addskincare.batch.import.edit.notice.desc"
              values={{
                type:
                  type === 'product'
                    ? t('p.s.addskincare.batch.import.product.singular', {
                        defaultValue: 'product',
                      })
                    : t('p.s.addskincare.batch.import.treatment.singular', {
                        defaultValue: 'treatment',
                      }),
              }}
            >
              Please export the current {{type}} list, revise the information,
              and then upload it for batch editing. Supported file types include
              .xlsx and zip (a compressed format containing xlsx and image
              files).
            </Trans>
          )}
          {step === 1 && (
            <>
              <div className="kt-margin-t-20 kt-margin-b-20">
                <h6 className="kt-margin-b-10">
                  {action === 'add' ? (
                    <Trans i18nKey="p.c.c.batch.import.step1">
                      Step 1. Download the template
                    </Trans>
                  ) : (
                    <Trans
                      i18nKey="p.s.addskincare.batch.import.step1.export"
                      values={{
                        type:
                          type === 'product'
                            ? t('p.s.addskincare.batch.import.product.plural', {
                                defaultValue: 'products',
                              })
                            : t(
                                'p.s.addskincare.batch.import.treatment.plural',
                                {
                                  defaultValue: 'treatments',
                                }
                              ),
                      }}
                    >
                      Step 1. Export the {{type}}
                    </Trans>
                  )}
                </h6>
                {step1Button()}
              </div>
              <div className="kt-margin-t-20 kt-margin-b-20">
                <h6 className="kt-margin-b-10">
                  <Trans i18nKey="p.c.c.batch.import.step2">
                    Step 2. Upload the file
                  </Trans>
                </h6>
                <div className="dropzone">
                  <Dropzone
                    accept=".csv, .xlsx, .zip"
                    multiple={false}
                    onDrop={file => {
                      if (file.length > 0) {
                        setFile(file[0]);
                        setTip('success');
                      } else setTip('fail');
                    }}
                  >
                    {({getRootProps, getInputProps}) => (
                      <section>
                        <div {...getRootProps()}>
                          <input {...getInputProps()} />
                          <div className="d-flex" style={{margin: 'auto auto'}}>
                            <div>
                              <i
                                className="fa fa-cloud-upload-alt"
                                style={{fontSize: '32px'}}
                              />
                            </div>
                            <div
                              className="text-center"
                              style={{width: '100%', fontSize: '20px'}}
                            >
                              <Trans i18nKey="p.c.c.batch.import.drag.drop">
                                Drag Drop a file here or Click to browse a file
                              </Trans>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}
                  </Dropzone>
                </div>
                {tipSection(tip)}
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div className="kt-margin-t-20 kt-margin-b-20">
                <h6 className="kt-margin-b-10">
                  <Trans i18nKey="p.c.c.batch.import.step3">
                    Step 3. Check the data and Upload
                  </Trans>
                </h6>
                <div className="d-flex kt-margin-t-10 kt-margin-b-10 align-items-center">
                  <Trans i18nKey="p.c.c.batch.import.file.name">
                    File Name:
                  </Trans>
                  {file && (
                    <a
                      href={URL.createObjectURL(file)}
                      className="kt-margin-l-10"
                      style={{textDecoration: 'underline'}}
                    >
                      {file.path || file.name}
                    </a>
                  )}
                  <div className="kt-margin-l-10">
                    <FileUploader
                      className={'btn btn-primary btn-sm'}
                      text={
                        <Trans i18nKey="p.c.c.batch.import.choose.other.file">
                          Choose other file
                        </Trans>
                      }
                      handleFile={f => {
                        setFile(f);
                        validateFile(f, type);
                      }}
                      accept=".xlsx, .zip"
                    />
                  </div>
                </div>
                {finishUpload ? (
                  <UploadResultMessage
                    uploadResponse={uploadResponse}
                    finishUpload={finishUpload}
                    isEdit={isEdit}
                  />
                ) : isValidating ? (
                  <div className="d-flex justify-content-center kt-margin-20">
                    <Loader />
                  </div>
                ) : (
                  <div className="kt-section">
                    <div
                      className="kt-section__content kt-section__content--solid"
                      style={{
                        borderLeft: readyToUpload
                          ? '4px solid  #1dc9b7'
                          : '4px solid  #fd397a',
                        backgroundColor: readyToUpload
                          ? 'rgba(29, 201, 183, 0.1)'
                          : 'rgba(253, 57, 122, 0.1)',
                      }}
                    >
                      <div className="d-flex kt-margin-t-10 kt-margin-b-10">
                        <label className="d-flex align-items-center kt-font-bold">
                          {readyToUpload ? (
                            <>
                              <i
                                className="fa fa-check-square text-success kt-margin-r-10"
                                style={{fontSize: '28px'}}
                              />
                              <Trans
                                i18nKey="p.s.addskincare.batch.import.check.success"
                                values={{
                                  rows: rows,
                                  type:
                                    type === 'product'
                                      ? t(
                                          'p.s.addskincare.batch.import.product.title.singular',
                                          {
                                            defaultValue: 'Product',
                                          }
                                        )
                                      : t(
                                          'p.s.addskincare.batch.import.treatment.title.singular',
                                          {
                                            defaultValue: 'Treatment',
                                          }
                                        ),
                                }}
                              >
                                All {{rows}} {{type}} records you are about to
                                import meet the required format. Click "Upload"
                                to submit the {{type}} data to Skincare Pro.
                              </Trans>
                            </>
                          ) : (
                            <>
                              <i
                                className="fa fa-exclamation-triangle text-danger kt-margin-r-10"
                                style={{fontSize: '28px'}}
                              />
                              <Trans i18nKey="p.c.c.batch.import.check.fail">
                                There is something wrong with the file. Please
                                check the errors below.
                              </Trans>
                            </>
                          )}
                        </label>
                      </div>
                      {validateErrors && validateErrors.errorType && (
                        <ul>
                          {renderErrorMessages(validateErrors).map(
                            (msg, index) => (
                              <li key={index}>{msg}</li>
                            )
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          <div className="d-flex justify-content-end">
            <button
              className="btn btn-secondary kt-margin-r-10"
              onClick={async () => {
                closeModal();
                if (refetch) {
                  await refetch();
                }
              }}
            >
              <Trans i18nKey="c.f.simple.button.group.discard">Discard</Trans>
            </button>
            <InfoDialog
              trigger={tip !== 'success' && step === 1}
              placement={'top'}
              description={
                <Trans i18nKey="p.c.c.batch.import.no.file.upload">
                  There is no file upload, please check again.
                </Trans>
              }
            >
              <div
                style={{
                  cursor: 'not-allowed',
                }}
              >
                <button
                  disabled={
                    (step === 1 && tip !== 'success') ||
                    (step === 2 && !readyToUpload) ||
                    isValidating ||
                    isUploading
                  }
                  className={classnames({
                    btn: true,
                    'btn-primary': true,
                    'kt-spinner': true,
                    'kt-spinner-v2': true,
                    'kt-spinner--center': true,
                    'kt-spinner--sm': true,
                    'kt-spinner--warning': isValidating || isUploading,
                  })}
                  onClick={() => {
                    if (step === 1) {
                      setStep(2);
                      validateFile(file, type);
                    } else if (step === 2 && finishUpload) {
                      closeModal();
                      if (refetch) {
                        refetch();
                      }
                    } else {
                      uploadFile(completeData);
                    }
                  }}
                >
                  {step === 1 ? (
                    <Trans i18nKey="c.f.simple.button.group.next">Next</Trans>
                  ) : step === 2 && finishUpload ? (
                    <Trans i18nKey="tutorials.tooltip.done">Done</Trans>
                  ) : (
                    <Trans i18nKey="p.c.c.batch.import.upload">Upload</Trans>
                  )}
                </button>
              </div>
            </InfoDialog>
          </div>
        </Modal.Body>
      </Modal>
      {isUploading && readyToUpload && <OverLayLoader />}
    </>
  );
}
